import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Avatar, Button, Space} from 'antd';
import {PageHeader, useAnnouncedMessage} from '@dowty/ui';
import {AccountDataPanel} from '@dowty/legal';
import {AddFriendRequest, UserDatas} from '../generated/user_pb';
import {UsernatorClient} from '../generated/user_grpc_web_pb';
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS} from '../grpcConfig';
import {FriendListCard} from '../Components/FriendCard';
import {tryParseUserDataFromLocalStorage} from '../utils/userStorage';
import {cancelAccountDeletion, exportMyData, getDeletionStatus, requestAccountDeletion} from '../Services/accountDataClient';
import {isGerman, text} from '../utils/language';
import {encodeMessage} from '../offline';

const UserPage: React.FC = () => {
    const [message, messageHolder] = useAnnouncedMessage();
    const [username, setUsername] = useState('Test');
    const [userId, setUserId] = useState('12345');
    const [friends, setFriends] = useState<string[]>([]);
    const [deletionScheduledFor, setDeletionScheduledFor] = useState<string | null>(null);
    const [deletionStatusLoading, setDeletionStatusLoading] = useState(true);
    const [deletionStatusError, setDeletionStatusError] = useState(false);

    const refreshDeletionStatus = useCallback(async () => {
        setDeletionStatusLoading(true);
        setDeletionStatusError(false);
        try {
            setDeletionScheduledFor(await getDeletionStatus());
        } catch {
            setDeletionStatusError(true);
        } finally {
            setDeletionStatusLoading(false);
        }
    }, []);

    useEffect(() => {
        const user = tryParseUserDataFromLocalStorage();
        if (user) {
            setUsername(user.getUsername());
            setUserId(user.getUserid().toString());
        }
        const storedFriends = localStorage.getItem('friends');
        if (storedFriends) setFriends(UserDatas.deserializeBinary(Uint8Array.from(atob(storedFriends), c => c.codePointAt(0)!)).getUserList().map(friend => friend.getUsername()));
        void refreshDeletionStatus();
    }, [refreshDeletionStatus]);

    const handleAddFriend = async (friendName: string): Promise<boolean> => {
        try {
            const allUsersString = localStorage.getItem('allUsers');
            const user = tryParseUserDataFromLocalStorage();
            if (!allUsersString || !user) {
                message.error(text('Benutzerdaten fehlen. Bitte die Seite neu laden.', 'User data is unavailable. Reload the page and try again.'));
                return false;
            }
            const friend = UserDatas.deserializeBinary(Uint8Array.from(atob(allUsersString), c => c.codePointAt(0)!))
                .getUserList().find(candidate => candidate.getUsername() === friendName.trim());
            if (!friend) {
                message.error(text('Benutzer nicht gefunden. Bitte den Namen prüfen.', 'User not found. Check the username.'));
                return false;
            }
            if (friend.getUserid() === user.getUserid()) {
                message.error(text('Du kannst dich nicht selbst hinzufügen.', 'You cannot add yourself as a friend.'));
                return false;
            }
            if (friends.includes(friend.getUsername())) {
                message.error(text('Dieser Benutzer ist bereits dein Freund.', 'This user is already your friend.'));
                return false;
            }
            const request = new AddFriendRequest();
            request.setUser(user);
            request.setTobefriendid(friend.getUserid());
            await new Promise<void>((resolve, reject) => {
                new UsernatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS).addFriend(request, undefined, (error, response) => {
                    if (error || !response) { reject(error); return; }
                    resolve();
                });
            });
            const storedFriends = localStorage.getItem('friends');
            const friendsData = storedFriends ? UserDatas.deserializeBinary(Uint8Array.from(atob(storedFriends), c => c.codePointAt(0)!)) : new UserDatas();
            if (!friendsData.getUserList().some(item => item.getUserid() === friend.getUserid())) friendsData.addUser(friend);
            localStorage.setItem('friends', encodeMessage(friendsData));
            setFriends(friendsData.getUserList().map(item => item.getUsername()));
            message.success(text('Freund hinzugefügt.', 'Friend added.'));
            return true;
        } catch {
            message.error(text('Freund konnte nicht hinzugefügt werden. Bitte erneut versuchen.', 'Could not add friend. Please try again.'));
            return false;
        }
    };

    const profileInitial = username[0]?.toUpperCase() ?? '?';

    return <div className="sf-user-page">
        {messageHolder}
        <PageHeader title={username} subtitle={`ID: ${userId}`} extra={<Avatar>{profileInitial}</Avatar>} />
        <FriendListCard friends={friends} onAddFriend={handleAddFriend} />
        <Space direction="vertical" size={16} style={{width: '100%'}}>
            {deletionStatusLoading && <Alert type="info" showIcon role="status" message={isGerman() ? 'Kontostatus wird geladen …' : 'Checking your account status …'} />}
            {deletionStatusError && <Alert type="error" showIcon message={isGerman() ? 'Der Kontostatus konnte nicht geladen werden.' : 'Could not load your account status.'} action={<Button type="primary" size="large" onClick={() => void refreshDeletionStatus()}>{isGerman() ? 'Erneut versuchen' : 'Retry'}</Button>} />}
            <AccountDataPanel
                language={isGerman() ? 'de' : 'en'}
                deletionScheduledFor={deletionScheduledFor}
                onExport={exportMyData}
                onRequestDeletion={requestAccountDeletion}
                onCancelDeletion={cancelAccountDeletion}
                onChanged={() => { void refreshDeletionStatus(); }}
            />
        </Space>
    </div>;
};

export default UserPage;
